import React, { useState, useEffect } from 'react';
import { useProjects } from '../../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, X, Save, Loader2, GripVertical, Users, Globe, Tag, GitBranch } from 'lucide-react';

const ProjectSettings = () => {
  const { getSettings, updatePipelineStages, updatePlatforms, updateDefaultMembers, getUsersLookup, getTags } = useProjects();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  
  // Pipeline stages
  const [stages, setStages] = useState([]);
  const [newStage, setNewStage] = useState('');
  
  // Platforms
  const [platforms, setPlatforms] = useState([]);
  const [newPlatform, setNewPlatform] = useState('');
  
  // Default team
  const [defaultMembers, setDefaultMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  
  // Tags (read-only with usage count)
  const [tags, setTags] = useState([]);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      const [settings, users, tagsData] = await Promise.all([
        getSettings(),
        getUsersLookup(),
        getTags()
      ]);
      if (settings) {
        setStages(settings.pipeline_stages || []);
        setPlatforms(settings.platforms || []);
        setDefaultMembers(settings.default_team_members || []);
      }
      setAllUsers(users || []);
      setTags(tagsData || []);
      setLoading(false);
    };
    loadSettings();
  }, []);

  // Pipeline stages
  const addStage = () => {
    if (newStage.trim() && !stages.includes(newStage.trim())) {
      setStages(prev => [...prev, newStage.trim()]);
      setNewStage('');
    }
  };

  const removeStage = (stage) => {
    setStages(prev => prev.filter(s => s !== stage));
  };

  const moveStage = (index, direction) => {
    const newStages = [...stages];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newStages.length) return;
    [newStages[index], newStages[swapIndex]] = [newStages[swapIndex], newStages[index]];
    setStages(newStages);
  };

  const saveStages = async () => {
    setSaving('stages');
    const result = await updatePipelineStages(stages);
    setSaving('');
    if (result.success) toast.success('Pipeline stages saved');
    else toast.error(result.error);
  };

  // Platforms
  const addPlatform = () => {
    if (newPlatform.trim() && !platforms.includes(newPlatform.trim())) {
      setPlatforms(prev => [...prev, newPlatform.trim()]);
      setNewPlatform('');
    }
  };

  const removePlatform = (platform) => {
    setPlatforms(prev => prev.filter(p => p !== platform));
  };

  const savePlatforms = async () => {
    setSaving('platforms');
    const result = await updatePlatforms(platforms);
    setSaving('');
    if (result.success) toast.success('Platforms saved');
    else toast.error(result.error);
  };

  // Default members
  const toggleMember = (userId) => {
    setDefaultMembers(prev =>
      prev.includes(userId) ? prev.filter(u => u !== userId) : [...prev, userId]
    );
  };

  const saveMembers = async () => {
    setSaving('members');
    const result = await updateDefaultMembers(defaultMembers);
    setSaving('');
    if (result.success) toast.success('Default team saved');
    else toast.error(result.error);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Pipeline Stages */}
      <Card data-testid="settings-pipeline">
        <CardHeader>
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-blue-600" />
            <div>
              <CardTitle className="text-base">Pipeline Stages</CardTitle>
              <CardDescription>Configure the project pipeline stages. Drag to reorder.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            {stages.map((stage, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border">
                <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
                <span className="flex-1 text-sm font-medium">{stage}</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStage(idx, -1)} disabled={idx === 0}>
                    <span className="text-xs">&#9650;</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStage(idx, 1)} disabled={idx === stages.length - 1}>
                    <span className="text-xs">&#9660;</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeStage(stage)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newStage}
              onChange={(e) => setNewStage(e.target.value)}
              placeholder="New stage name..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStage())}
              data-testid="new-stage-input"
            />
            <Button variant="outline" onClick={addStage}><Plus className="w-4 h-4" /></Button>
          </div>
          <Button onClick={saveStages} disabled={saving === 'stages'} className="bg-blue-600 hover:bg-blue-700" data-testid="save-stages-btn">
            {saving === 'stages' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Pipeline
          </Button>
        </CardContent>
      </Card>

      {/* Platforms */}
      <Card data-testid="settings-platforms">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-600" />
            <div>
              <CardTitle className="text-base">Platforms</CardTitle>
              <CardDescription>Manage the list of platforms where you send proposals.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {platforms.map((p, idx) => (
              <Badge key={idx} variant="outline" className="gap-1 px-3 py-1.5">
                {p}
                <X className="w-3 h-3 cursor-pointer text-red-400 hover:text-red-600" onClick={() => removePlatform(p)} />
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newPlatform}
              onChange={(e) => setNewPlatform(e.target.value)}
              placeholder="New platform..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPlatform())}
              data-testid="new-platform-input"
            />
            <Button variant="outline" onClick={addPlatform}><Plus className="w-4 h-4" /></Button>
          </div>
          <Button onClick={savePlatforms} disabled={saving === 'platforms'} className="bg-blue-600 hover:bg-blue-700" data-testid="save-platforms-btn">
            {saving === 'platforms' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Platforms
          </Button>
        </CardContent>
      </Card>

      {/* Default Team Members */}
      <Card data-testid="settings-default-team">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-green-600" />
            <div>
              <CardTitle className="text-base">Default Team Members</CardTitle>
              <CardDescription>These users will be automatically assigned to every new project.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-slate-50">
            {allUsers.map(user => (
              <Badge
                key={user.id}
                variant={defaultMembers.includes(user.id) ? "default" : "outline"}
                className={`cursor-pointer transition-colors ${defaultMembers.includes(user.id) ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-slate-100'}`}
                onClick={() => toggleMember(user.id)}
                data-testid={`member-toggle-${user.username}`}
              >
                {user.full_name || user.username}
              </Badge>
            ))}
          </div>
          <Button onClick={saveMembers} disabled={saving === 'members'} className="bg-blue-600 hover:bg-blue-700" data-testid="save-members-btn">
            {saving === 'members' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Default Team
          </Button>
        </CardContent>
      </Card>

      {/* Tags (Read-only) */}
      <Card data-testid="settings-tags">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-purple-600" />
            <div>
              <CardTitle className="text-base">Tags</CardTitle>
              <CardDescription>Tags are created automatically when used in projects. Usage counts are shown.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <p className="text-sm text-slate-400">No tags yet. Tags are created when you add them to projects.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, idx) => (
                <Badge key={idx} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 px-3 py-1.5">
                  {tag.name}
                  <span className="ml-1.5 text-[10px] bg-purple-200 text-purple-800 px-1.5 rounded-full">{tag.usage_count || 0}</span>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectSettings;
